import { type FC, useState, useRef, type MutableRefObject, type ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useIntl, defineMessages, FormattedMessage } from 'dibs-react-intl';
import MoreDots from 'dibs-icons/exports/legacy/MoreDots.ts';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { Tooltip } from 'dibs-elements/exports/Tooltip.tsx';
import { ModalContainer } from 'dibs-elements/exports/ModalContainer.tsx';
import { ModalCloseButton } from 'dibs-elements/exports/ModalCloseButton.tsx';
import { useNftItemTileContext } from '../../NftItemTileContext.tsx';
import { Modal, useModalHandlers } from '../NftItemTileSecondaryMarketButton.tsx';
import TransferTokenModal, { TRANSFER_TOKEN } from '../ActionModals/TransferTokenModal.tsx';
import BurnNftModal, { BURN_NFT } from '../ActionModals/BurnNftModal.tsx';
import DeleteDraftModal, { DELETE_DRAFT } from '../ActionModals/DeleteDraftModal.tsx';
import ActionToast from '../ActionModals/ActionToast.tsx';
import {
    type ModalAction,
    type TooltipAction,
    type SecondaryModalAction,
    GRANT_APPROVAL,
    CANCEL_AUCTION_SECONDARY,
    MANAGE_NFT,
    PREVIEW_DRAFT,
    REFRESH_METADATA,
} from '../ActionModals/types.ts';
import { useTransferToken } from '../../hooks/useTransferToken.ts';
import { useBurnNft } from '../../hooks/useBurnNft.ts';
import { useDeleteDraft } from '../../hooks/useDeleteDraft.ts';
import { useRefreshMetadata } from '../../hooks/useRefreshMetadata.ts';
import { GLOBAL_CLIENT_ONLY_LOCALE, handleLocaleUrl } from 'dibs-intl/exports/urls';
import { useItemInternalContext } from '../../helpers/ItemInternalContext.tsx';
import { useMarketContext } from 'dibs-crypto-utils/exports/useMarketContext.ts';
import { isFirstDibsTokenContract } from 'dibs-crypto-utils/exports/isFirstDibsTokenContract.ts';

import ReturnArrowIcon from 'dibs-icons/exports/legacy/ReturnArrow.ts';
import PenIcon from 'dibs-icons/exports/legacy/Pen.ts';
import BrokenImageIcon from 'dibs-icons/exports/legacy/BrokenImage.ts';
import TrashCanIcon from 'dibs-icons/exports/legacy/TrashCan.ts';
import EyeIcon from 'dibs-icons/exports/legacy/Eye.ts';
import EarlyAccessIcon from 'dibs-icons/exports/legacy/EarlyAccess.ts';
import CancelCircleIcon from 'dibs-icons/exports/legacy/CancelCircle.ts';
import ArrowClockwise from 'dibs-icons/exports/legacy/ArrowClockwise.ts';

import styles from './NftItemTileTooltip.scss';
import { type NftItemTileTooltip_viewer$key } from './__generated__/NftItemTileTooltip_viewer.graphql.ts';
import { type NftItemTileTooltip_nftItem$key } from './__generated__/NftItemTileTooltip_nftItem.graphql.ts';
import { type NftItemTileTooltip_nftItemDraft$key } from './__generated__/NftItemTileTooltip_nftItemDraft.graphql.ts';

const viewerFragment = graphql`
    fragment NftItemTileTooltip_viewer on Viewer {
        nftBenignNeglect: featureFlag(feature: "nftBenignNeglect")
    }
`;

const nftItemFragment = graphql`
    fragment NftItemTileTooltip_nftItem on NftItem {
        serviceId
        contract {
            addressWithFields {
                address
            }
        }
        balanceForUser(userId: $userId) @include(if: $fetchUser) {
            balance
        }
        isERC1155
        ...useTransferToken_nftItem
        ...useBurnNft_nftItem
        ...useRefreshMetadata_nftItem
        ...HeaderWithImage_nftItem
    }
`;

const nftItemDraftFragment = graphql`
    fragment NftItemTileTooltip_nftItemDraft on NftItemDraft {
        serviceId
        user {
            ethereumWalletAddress {
                publicProfileUrl
            }
        }
        ...HeaderWithImage_draft
    }
`;

const optionMessages = defineMessages({
    TRANSFER_TOKEN: { id: 'nftItemTileTooltip.option.transfer', defaultMessage: 'Transfer' },
    BURN_NFT: { id: 'nftItemTileTooltip.option.burn', defaultMessage: 'Burn' },
    MANAGE_NFT: { id: 'nftItemTileTooltip.option.manage', defaultMessage: 'Manage NFT' },
    DELETE_DRAFT: { id: 'nftItemTileTooltip.option.deleteDraft', defaultMessage: 'Delete' },
    PREVIEW_DRAFT: { id: 'nftItemTileTooltip.option.previewDraft', defaultMessage: 'Preview' },
    GRANT_APPROVAL: {
        id: 'nftItemTileTooltip.option.grantApproval',
        defaultMessage: 'Grant Approval',
    },
    CANCEL_AUCTION_SECONDARY: {
        id: 'nftItemTileTooltip.option.cancelAuction',
        defaultMessage: 'Cancel Auction',
    },
    REFRESH_METADATA: {
        id: 'nftItemTileTooltip.option.refreshMetaData',
        defaultMessage: 'Refresh Metadata',
    },
});

type Props = {
    viewer: NftItemTileTooltip_viewer$key;
    nftItem: NftItemTileTooltip_nftItem$key | null | undefined;
    nftItemDraft: NftItemTileTooltip_nftItemDraft$key | null | undefined;
    isMobile?: boolean;
};

const TooltipOption: FC<{ onClick: () => void; children: ReactNode }> = ({ onClick, children }) => {
    return (
        <Link underline="none" className={styles.text} onClick={onClick}>
            {children}
        </Link>
    );
};

export const NftItemTileTooltip: FC<Props> = ({
    viewer: viewerRef,
    nftItem: nftItemRef,
    nftItemDraft: nftItemDraftRef,
    isMobile = false,
}) => {
    const intl = useIntl();
    const [currentActionModal, setCurrentActionModal] = useState<
        ModalAction | SecondaryModalAction | null
    >(null);
    const [isTooltipVisible, setIsTooltipVisible] = useState(false);
    const linkRef = useRef<HTMLButtonElement>(null);
    const viewer = useFragment(viewerFragment, viewerRef);
    const nftItem = useFragment(nftItemFragment, nftItemRef);
    const nftItemDraft = useFragment(nftItemDraftFragment, nftItemDraftRef);
    const { auction, user } = useItemInternalContext();
    const { hasCheckedForApproval, isTokenApprovalGranted } = useMarketContext();

    const showManageNft =
        !auction?.isCountdownFinished &&
        (auction?.isLive ? user?.isAuctionSeller : user?.isItemOwner);

    const serviceId = nftItem?.serviceId || nftItemDraft?.serviceId;

    const manageUrl = `/nft-create/${serviceId}/`;
    const previewUrl = `/nft/art/preview-${serviceId}`;

    const isFirstDibsToken = isFirstDibsTokenContract(
        nftItem?.contract?.addressWithFields?.address
    );

    const { onOpen: onSecondaryOpen, ...hookProps } = useModalHandlers(serviceId || null);

    const { secondaryMarketRefetch, withWalletConnectionEnsuredViewer } = useNftItemTileContext();

    const [handleTransferTokenClick, transferTokenStatus, resetTransfer, transferredEditions] =
        useTransferToken(nftItem);
    const currentBalanceForEditions =
        (nftItem?.isERC1155 && nftItem?.balanceForUser?.balance) || null;

    const [handleBurn, burnStatus, burnedEditions] = useBurnNft(nftItem);
    const [handleDelete, deleteStatus, resetDelete] = useDeleteDraft(
        serviceId,
        nftItemDraft?.user?.ethereumWalletAddress?.publicProfileUrl || null
    );

    const [handleRefreshMetadata, refreshMetadataStatus, resetRefreshMetadata] =
        useRefreshMetadata(nftItem);

    const optionActions = {
        TRANSFER_TOKEN: () => setCurrentActionModal(TRANSFER_TOKEN),
        BURN_NFT: () => setCurrentActionModal(BURN_NFT),
        MANAGE_NFT: () => {
            window.location.href = handleLocaleUrl(manageUrl, GLOBAL_CLIENT_ONLY_LOCALE);
        },
        DELETE_DRAFT: () => {
            setCurrentActionModal(DELETE_DRAFT);
        },
        PREVIEW_DRAFT: () => {
            window.location.href = handleLocaleUrl(previewUrl, GLOBAL_CLIENT_ONLY_LOCALE);
        },
        GRANT_APPROVAL: () => {
            setCurrentActionModal(GRANT_APPROVAL);
            onSecondaryOpen();
        },
        CANCEL_AUCTION_SECONDARY: () => {
            setCurrentActionModal(CANCEL_AUCTION_SECONDARY);
            onSecondaryOpen();
        },
        REFRESH_METADATA: () => {
            handleRefreshMetadata();
        },
    };

    const optionIcons = {
        TRANSFER_TOKEN: <ReturnArrowIcon />,
        BURN_NFT: <BrokenImageIcon />,
        MANAGE_NFT: <PenIcon />,
        DELETE_DRAFT: <TrashCanIcon />,
        PREVIEW_DRAFT: <EyeIcon />,
        GRANT_APPROVAL: <EarlyAccessIcon />,
        CANCEL_AUCTION_SECONDARY: <CancelCircleIcon />,
        REFRESH_METADATA: <ArrowClockwise />,
    };

    const tooltipOptions: TooltipAction[] = [];
    if (nftItemDraft) {
        tooltipOptions.push(DELETE_DRAFT);
        tooltipOptions.push(PREVIEW_DRAFT);
    } else {
        if (showManageNft) {
            tooltipOptions.push(MANAGE_NFT);
        }
        if (!auction?.isCountdownFinished && user?.isItemOwner) {
            tooltipOptions.push(BURN_NFT);
            tooltipOptions.push(TRANSFER_TOKEN);
        }
        if (
            hasCheckedForApproval &&
            !isTokenApprovalGranted &&
            user?.isItemOwner &&
            !viewer.nftBenignNeglect
        ) {
            tooltipOptions.push(GRANT_APPROVAL);
        }
        if (auction?.isCancellable) {
            tooltipOptions.push(CANCEL_AUCTION_SECONDARY);
        }
        if (!isFirstDibsToken && (user?.isItemOwner || user?.isAuctionSeller)) {
            tooltipOptions.push(REFRESH_METADATA);
        }
    }

    if (tooltipOptions.length === 0) {
        return null;
    }

    return (
        <>
            <div className={styles.dropdownWrapper}>
                <Link
                    linkRef={linkRef as MutableRefObject<HTMLButtonElement>}
                    onClick={() =>
                        setIsTooltipVisible(prevIsTooltipVisible => !prevIsTooltipVisible)
                    }
                >
                    <MoreDots className={styles.moreDotsIcon} />
                </Link>
                {isTooltipVisible && !isMobile && (
                    <Tooltip
                        isVisible
                        direction="bottom"
                        type="autoWidth"
                        paddingSize="xSmall"
                        keepInViewport={false}
                        closeOnOutsideClick
                        onClose={e => {
                            //on IOS phones tooltip event listener fires before click https://github.com/1stdibs/ferrum/pull/11592
                            if (!linkRef.current?.contains(e.target as Node)) {
                                setIsTooltipVisible(false);
                            }
                        }}
                    >
                        <div className={styles.itemsContainer}>
                            {tooltipOptions?.map((option, index) => (
                                <TooltipOption
                                    key={index}
                                    onClick={() => {
                                        setIsTooltipVisible(false);
                                        optionActions[option]?.();
                                    }}
                                >
                                    <span className={styles.icon}>{optionIcons[option]}</span>
                                    {intl.formatMessage(optionMessages[option])}
                                </TooltipOption>
                            ))}
                        </div>
                    </Tooltip>
                )}
                {isTooltipVisible && isMobile && (
                    <ModalContainer
                        modalClass={styles.modalContainer}
                        isOpen={isTooltipVisible}
                        onClose={() => setIsTooltipVisible(false)}
                        type="slideBottom"
                        modalSize="medium"
                    >
                        <div className={styles.modalWrapper}>
                            <div className={styles.modalTitle}>
                                <FormattedMessage
                                    id="nftItemTileTooltip.mobile.title"
                                    defaultMessage="NFT"
                                />
                            </div>
                        </div>
                        {tooltipOptions?.map((option, index) => (
                            <div key={index} className={styles.mobileButtonWrapper}>
                                <Link
                                    className={styles.modalButton}
                                    onClick={() => {
                                        setIsTooltipVisible(false);
                                        optionActions[option]?.();
                                    }}
                                    buyerLinkType="linkNoDecoration"
                                >
                                    <span className={styles.actionIcon}>{optionIcons[option]}</span>{' '}
                                    {intl.formatMessage(optionMessages[option])}
                                </Link>
                            </div>
                        ))}
                        <ModalCloseButton onClick={() => setIsTooltipVisible(false)} />
                    </ModalContainer>
                )}
            </div>
            {currentActionModal === TRANSFER_TOKEN && (
                <TransferTokenModal
                    viewer={withWalletConnectionEnsuredViewer || undefined}
                    nftItem={nftItem || null}
                    onClick={handleTransferTokenClick}
                    reset={resetTransfer}
                    status={transferTokenStatus}
                    onClose={() => {
                        if (transferTokenStatus === 'success') {
                            secondaryMarketRefetch?.({ refetchDrafts: true });
                        }
                        setCurrentActionModal(null);
                    }}
                    currentBalance={currentBalanceForEditions}
                />
            )}
            {currentActionModal === BURN_NFT && (
                <BurnNftModal
                    viewer={withWalletConnectionEnsuredViewer || undefined}
                    nftItem={nftItem || null}
                    onClose={() => {
                        setCurrentActionModal(null);
                        if (burnStatus === 'success') {
                            secondaryMarketRefetch?.({ refetchDrafts: true });
                        }
                        setCurrentActionModal(null);
                    }}
                    onClick={handleBurn}
                    status={burnStatus}
                    currentBalance={currentBalanceForEditions}
                />
            )}
            {currentActionModal === DELETE_DRAFT && (
                <DeleteDraftModal
                    onClose={() => {
                        if (deleteStatus === 'success') {
                            setCurrentActionModal(null);
                            resetDelete();
                            secondaryMarketRefetch?.({ refetchDrafts: true });
                        } else if (deleteStatus !== 'inflight') {
                            // do not close the modal if loading
                            setCurrentActionModal(null);
                        }
                    }}
                    nftItemDraft={nftItemDraft}
                    publicProfileUrl={
                        nftItemDraft?.user?.ethereumWalletAddress?.publicProfileUrl || ''
                    }
                    status={deleteStatus}
                    onClick={handleDelete}
                />
            )}
            {(currentActionModal === GRANT_APPROVAL ||
                currentActionModal === CANCEL_AUCTION_SECONDARY) && (
                <Modal isMobile={isMobile || false} hideFallback {...hookProps} />
            )}
            {!currentActionModal && (
                <>
                    <ActionToast
                        isActive={!!refreshMetadataStatus}
                        action={REFRESH_METADATA}
                        status={refreshMetadataStatus}
                        onClose={() => {
                            if (refreshMetadataStatus === 'success') {
                                resetRefreshMetadata();
                            }
                        }}
                    />
                    <ActionToast
                        isActive={!!burnStatus}
                        action={BURN_NFT}
                        status={burnStatus}
                        onClose={() => {
                            if (burnStatus === 'success') {
                                location.assign(
                                    handleLocaleUrl('/nft/profile', GLOBAL_CLIENT_ONLY_LOCALE)
                                );
                            }
                        }}
                        affectedEditions={burnedEditions}
                    />
                    <ActionToast
                        isActive={!!transferTokenStatus}
                        action={TRANSFER_TOKEN}
                        status={transferTokenStatus}
                        onClose={() => {
                            if (transferTokenStatus === 'success') {
                                location.assign(
                                    handleLocaleUrl('/nft/profile', GLOBAL_CLIENT_ONLY_LOCALE)
                                );
                            }
                        }}
                        affectedEditions={transferredEditions}
                    />
                </>
            )}
        </>
    );
};
